import React from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';
import { TEXT } from '../../uiTokens';

/**
 * Mic control for on-device dictation. Renders nothing unless the browser can
 * transcribe locally, so audio never reaches a third-party service.
 */
export default function DictateButton({ dictation, disabled = false }) {
  const {
    status, interim, error, isSupported, isListening, isBusy, toggle,
  } = dictation;

  if (!isSupported) return null;

  const label = isListening
    ? 'Stop dictating'
    : isBusy
      ? 'Preparing dictation'
      : status === 'downloadable'
        ? 'Set up dictation'
        : 'Dictate secret';

  // Speech-to-text handles passphrases well and symbol-heavy passwords poorly.
  const title = isListening || isBusy ? label : `${label}. Works best for passphrases and notes.`;

  const Icon = isListening ? Square : isBusy ? Loader2 : Mic;

  const tone = isListening
    ? 'bg-accent text-accent-fg'
    : 'text-muted hover:text-fg hover:bg-fg/5';

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        disabled={disabled || isBusy}
        aria-pressed={isListening}
        aria-label={label}
        title={title}
        className={`relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${tone}`}
      >
        {isListening && (
          <span className="absolute inset-1.5 rounded-lg bg-accent-fg/25 animate-ping" aria-hidden="true" />
        )}
        <Icon
          className={`relative h-[18px] w-[18px] ${isBusy ? 'animate-spin' : ''}`}
          strokeWidth={2}
          aria-hidden="true"
        />
      </button>

      <p className="sr-only" role="status" aria-live="polite">
        {isListening ? `Listening. ${interim}` : ''}
      </p>

      {(isListening || isBusy || error || status === 'downloadable') && (
        <p className={`${TEXT.caption} ${error ? 'text-danger' : ''} w-full`}>
          {error
            || (isListening && (interim.trim() || 'Listening. Transcribed on your device, audio never leaves this browser.'))
            || (isBusy && 'Downloading the on-device language pack, one time only.')
            || 'Dictate instead of typing. Setup downloads a language pack once, then everything stays on your device.'}
        </p>
      )}
    </>
  );
}
